import { defineComponent as _defineComponent } from 'vue'
import { type Maybe, SimilarTitlesEdge } from '@/@types/graphql-types';
import type { ThumborOptions } from '@/filters/thumbor';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleFilter } from '@/@types/graphql-types';
import type { CatalogueItem } from '@/helpers/promotion-helper';
import type { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';

import PictureComp from '@/components/picture/PictureComp.vue';
import TitleScoreList from '@/components/TitleScoreList.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PromotionRowCTA from '@/features/buybox/components/list/PromotionRowCTA.vue';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import DisclaimerButton from '@/features/buybox/components/DisclaimerButton.vue';

import { ScoringProvider, getEffectiveJwRating } from '@/helpers/scoring-helper';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { SnowplowSportContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { BuyboxParam, OfferLocation, trackClickOutSports } from '@/helpers/offer-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ObjectType, SportObjectType } from '@/@types/graphql-types';
import type { SportsTargetEvent } from '@/features/buybox/types/types';

import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { getPrimeAssociatesSuffix, primeImpactCountries } from '@/helpers/promotion-helper';

import { useQTVCompositionId } from '@/features/buybox/composables/useQTV';
// EXP_LINEAR_TV_2
import { useLinearTVExp } from '@/components/experiments/LinearTVExp';
// EXP_LINEAR_TV_2
// EXP_TITLE_PAGE_CATEGORIZATION
import { useTitlePageCategorizationExp } from '@/components/experiments/TitlePageCategorizationExp';
// EXP_TITLE_PAGE_CATEGORIZATION
import { useImpressionPixel } from '@/features/impression-pixels/composables/useImpressionPixel';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';

import Vue, { computed, inject, ref } from 'vue';

import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useAdServer } from '@/features/ad-server/composables/useAdServer';
import type { AdServerSqtvCreativeFragment } from '@/features/ad-server/graphql/fragments/AdServerSQTVCreative.fragment';
import { useNativeAds } from '@/features/native-ads/composables/useNativeAds';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { openJwTvAvodModal } from '@/features/jwtv-avod/helpers/jwtv-avod-helper';
import { ScoringFragment } from '@/pages/graphql/fragments/Scoring.fragment';


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleQTV',
  props: {
    title: null,
    catalogueItem: null,
    additionalContexts: null,
    sqtvProviderShortName: { default: null },
    swiperIndex: null,
    displayPromotedTag: { type: Boolean, default: true },
    providerFilter: null,
    hideFreeTrialTag: { type: Boolean },
    customTargetTitle: null,
    hideRatings: { type: Boolean },
    customTags: null,
    source: { default: 'sqtv' },
    preventImpressionTracking: { type: Boolean },
    nativeAdFallback: { type: Boolean },
    adServerCreative: null,
    sportsTargetEvent: null
  },
  emits: ["onPosterClickOut", "imageError", "onWatchNowClick"],
  setup(__props: any) {

const props = __props as { title: TitleDetail, catalogueItem: CatalogueItem | null, additionalContexts: SnowplowContext[], sqtvProviderShortName: string | null, swiperIndex?: number, displayPromotedTag: boolean, providerFilter?: TitleFilter, hideFreeTrialTag?: boolean, customTargetTitle?: TitleDetail, hideRatings?: boolean, customTags?: string[], source: 'sqtv' | 'sr_sqtv', preventImpressionTracking?: boolean, nativeAdFallback?: boolean, adServerCreative?: AdServerSqtvCreativeFragment, sportsTargetEvent?: SportsTargetEvent };





const { country, webLocale } = storeToRefs(useLanguageStore());
const { xServeTrack } = storeToRefs(useAppStore());

const sqtvClickoutType = computed(() => (props.source === 'sr_sqtv' ? 'sr_sqtv_watch_now' : props.source));
const { clickoutType, placement } = getClickoutTrackingPlacement(sqtvClickoutType.value);
const { setSQTVFallbackTag } = useNativeAds({ title: () => props.title });
// EXP_LINEAR_TV_2
const { setLinearTVExpTags } = useLinearTVExp();
// EXP_LINEAR_TV_2
// EXP_TITLE_PAGE_CATEGORIZATION
const { setTitlePageCategorizationExpTags } = useTitlePageCategorizationExp();
// EXP_TITLE_PAGE_CATEGORIZATION

const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | undefined>(undefined));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));

const { adServerIds, adServerStatus } = useAdServer(() => props.title.id);

/** pixel impression tracking for providers */
const providerShortName = computed(() => props.sqtvProviderShortName ?? props.catalogueItem?.shortName ?? null);

const singleQtvRef = ref(null);
useImpressionPixel(singleQtvRef, webLocale, { shortNames: providerShortName });

const isJWTVOffer = computed(() => providerShortName.value === 'jwt');
const hasFreeTrial = computed(() => !['dnp', 'mxx', 'jwt'].includes(providerShortName.value || ''));
const isFreeOrFreeTrial = computed(() => hasFreeTrial.value || providerShortName.value === 'jwt');

const {
	similarTitle: _similarTitle,
	loading,
	sqtvBannerAndDisclaimer,
	getSQTVTags,
} = useSingleQTV(
	() => props.title,
	providerShortName,
	// Wait for adserver to resolve before enabling — prevents a wasted fetch that gets cancelled
	// when the creative arrives. 'disabled' means not an active market, so fire immediately.
	() => !props.customTargetTitle && !props.adServerCreative && adServerStatus.value !== 'loading',
	props.providerFilter
);

const isAwaitingContent = computed(
	() => (!props.customTargetTitle && !props.adServerCreative && loading.value) || adServerStatus.value === 'loading'
);

const similarTitle = computed(() => {
	if (props.customTargetTitle) {
		return { node: props.customTargetTitle };
	}
	if (props.adServerCreative) {
		return {
			__typename: 'SimilarTitlesEdge' as const,
			node: props.adServerCreative.similarTitle,
			similarityScore: props.adServerCreative.similarityScore,
		};
	}
	return _similarTitle.value;
});

const scores = computed(() => (similarTitle.value?.node?.content?.scoring ?? {}) as ScoringFragment);

const excludedScores = computed(() => {
	const excluded = [];

	if ((scores.value?.imdbScore || 0) < 7) excluded.push(ScoringProvider.IMDB);
	if ((getEffectiveJwRating(scores.value) || 0) < 0.7) excluded.push(ScoringProvider.TMDB);
	if ((scores.value?.tomatoMeter || 0) < 70) excluded.push(ScoringProvider.ROTTEN_TOMATOES);

	return excluded.length === 3 ? excluded.slice(0, 1) : excluded;
});

const sourcePosterUrl = computed(() => props.title?.content?.posterUrl || '');
const posterUrl = computed(() => similarTitle.value?.node?.content?.posterUrl || '');
const backdropUrl = computed(() => similarTitle.value?.node?.content?.backdrops?.[0]?.backdropUrl || '');
const votesNumber = computed(() => similarTitle.value?.node?.content?.interactions?.votesNumber || 0);

const offer = computed(() => {
	// Hardcoded sports campaigns (e.g. F1 on Apple TV+) carry their own offer, no catalogueItem involved
	// Cast keeps this computed's type unchanged for the rest of the component (e.g. handleClickout's jwt branch)
	if (props.sportsTargetEvent) return props.sportsTargetEvent.offer as any;

	// For SR campaigns with customTargetTitle, get offer from catalogueItem
	// Note - when Hassan is back, i'd like to understand this better. I dont see why catalogue item is relevant to SR
	if (props.customTargetTitle && props.catalogueItem?.offer) {
		return props.catalogueItem.offer(null, country.value, true);
	}

	// For adserver creative, offers live on similarTitle
	if (props.adServerCreative) return props.adServerCreative.similarTitle.offers[0];

	// For regular SQTV, use the query result directly — _similarTitle always has offers
	const offers = _similarTitle.value?.node?.offers || [];

	return offers[0];
});

const jwRawEntityId = computed(() => similarTitle.value?.node.id ?? 'nsid');

const { getQTVCompositionId } = useQTVCompositionId();

const compositionId = computed(() => {
	const sourceTitleID = props.title.id;
	const similarTitleID = similarTitle.value?.node.id ?? 'nsid';
	const shortName = providerShortName.value === 'amp' ? 'prv' : providerShortName.value;

	return getQTVCompositionId(shortName, sourceTitleID, similarTitleID, 'sqtv');
});

const similarityScore = computed(() =>
	isSimilarTitlesEdge(similarTitle.value) ? similarTitle.value.similarityScore : null
);

const recommenderContext = computed(() => {
	const seasonNumber = props.title.__typename === 'Season' ? props.title.content.seasonNumber : null;
	const titleId = props.title.__typename === 'Season' ? props.title.show.id : props.title.id;

	return TrackingHelper.getRecommenderContext(
		props.source,
		titleId,
		seasonNumber,
		null,
		compositionId.value,
		null,
		null,
		null,
		similarityScore.value
	);
});

const similarTitleContext = computed(() => {
	if (!similarTitle.value || props.sportsTargetEvent) return null;
	return new SnowplowTitleContextGraphql(
		similarTitle.value.node.objectId,
		similarTitle.value.node.objectType,
		null,
		null,
		null,
		0
	);
});

const sportsContext = computed(() => {
	if (!props.sportsTargetEvent) return null;
	const context = new SnowplowSportContext(props.sportsTargetEvent.sportType, ObjectType.SportEvent);
	context.competitionId = props.sportsTargetEvent.competitionId;
	context.seasonId = props.sportsTargetEvent.seasonId;
	context.eventId = props.sportsTargetEvent.eventId;
	context.venueId = props.sportsTargetEvent.venueId;
	return context;
});

const clickoutUrl = computed(() => {
	if (!offer.value) return null;

	if (props.sportsTargetEvent) {
		return new ClickoutUrl(offer.value)
			.set('uct_country', country.value)
			.set('uct_buybox', BuyboxParam.Normal)
			.set('uct_sport', props.sportsTargetEvent.sportType)
			.addContexts(props.additionalContexts)
			.addContexts(sportsContext.value)
			.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
			.updateContext('ClickoutContextGraphql', 'placement', placement)
			.setCanary(xServeTrack.value);
	}

	if (props.source === 'sr_sqtv') {
		return new ClickoutUrl(offer.value)
			.addContexts(props.additionalContexts)
			.addContexts(titleContext.value)
			.skipRedirect()
			.set('uct_country', country.value)
			.updateContext('ClickoutContextGraphql', 'clickoutType', sqtvClickoutType.value)
			.updateContext('ClickoutContextGraphql', 'placement', 'sr_buybox')
			.setAdIds(props.adServerCreative ? adServerIds.value : null);
	}

	const clickoutUrl = new ClickoutUrl(offer.value)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Normal)
		.set('uct_ft', '1')
		.addContexts(props.additionalContexts)
		.addContexts(similarTitleContext.value)
		.setPageType(similarTitle.value?.node.objectType ?? props.title.objectType)
		.setTitleSourceId(similarTitle.value?.node.id ?? null)
		.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
		.setTitleSourceId(similarTitle.value?.node?.id ?? null)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.updateContext('ClickoutContextGraphql', 'swiperIndex', props.swiperIndex)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.addContexts(recommenderContext.value)
		.setCanary(xServeTrack.value)
		.setAdIds(props.adServerCreative ? adServerIds.value : null);

	// Prime Associates - Impact, and all other providers already handled by UCT2
	if (['amp', 'prv'].includes(providerShortName.value || '') && !primeImpactCountries.includes(country.value)) {
		const suffix = getPrimeAssociatesSuffix(country.value);
		const type = similarTitle.value?.node.objectType === 'MOVIE' ? 'mov' : 'sho';
		const tag = `amazon-${country.value.toLowerCase()}-sqtv-${type}-${suffix}`;

		clickoutUrl
			.set('uct_ctag', tag)
			.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	if (props.nativeAdFallback) {
		clickoutUrl.apply(u => setSQTVFallbackTag(u, providerShortName.value || ''));
	}

	// EXP_LINEAR_TV_2
	clickoutUrl.apply(u =>
		setLinearTVExpTags({
			url: u,
			type: 'ft',
			shortName: providerShortName.value || '',
			technicalName: offer.value?.package.technicalName,
		})
	);
	// EXP_LINEAR_TV_2

	// EXP_TITLE_PAGE_CATEGORIZATION
	clickoutUrl.apply(u =>
		setTitlePageCategorizationExpTags({
			url: u,
			placement: 'sqtv',
			shortName: providerShortName.value || '',
		})
	);
	// EXP_TITLE_PAGE_CATEGORIZATION

	return clickoutUrl;
});

const tags = computed(() => {
	if (props.customTags?.length) return props.customTags;
	return getSQTVTags({
		title: props.title,
		similarTitle: similarTitle.value?.node,
		monetizationType: offer.value?.monetizationType,
		allowFreeTag: hasFreeTrial.value && !props.hideFreeTrialTag,
	});
});

/* VISIBILITY TRACKING */
const observeVisibilityOptions = computed(() => {
	if (props.preventImpressionTracking) {
		return false;
	}
	return {
		...observableOptions,
		callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
	};
});

const baseContexts = computed(() => [...props.additionalContexts, recommenderContext.value]);
const recommenderImpressionContexts = computed(() =>
	similarTitleContext.value ? [...baseContexts.value, similarTitleContext.value] : baseContexts.value
);

const offerImpressionContexts = computed(() => {
	const contexts = titleContext.value ? [...baseContexts.value, titleContext.value] : baseContexts.value;
	// Hardcoded sports campaigns (e.g. F1 on Apple TV+) — SportContext is added to the clickout URL's own
	// contexts, but offer impressions are tracked via additionalContexts, not clickoutUrl.contexts, so it
	// needs to be added here too for the impression to carry it.
	return sportsContext.value ? [...contexts, sportsContext.value] : contexts;
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: offerImpressionContexts,
	groupId: 'sqtv',
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible) return;

	// ML Recommender (similar titles) impression tracking
	TrackingHelper.trackEvent(
		'recommender_feature',
		{
			action: 'impression',
			value: 1,
			nonInteraction: true,
		},
		recommenderImpressionContexts.value
	);

	if (clickoutUrl.value) {
		const additionalContext = [similarTitleContext.value];
		onIntersection(clickoutUrl.value, isVisible);
	}
}
/* VISIBILITY TRACKING */

function imageUrl(titlePosterUrl: string | null | undefined, type: 'poster' | 'backdrop' = 'poster') {
	if (!titlePosterUrl) {
		return undefined;
	}
	return Vue.filter('Thumbor')({
		url: titlePosterUrl,
		imageType: type,
	} as ThumborOptions)?.replace('{format}', 'jpg');
}
const { openRentalModal } = useRentalModal();
async function handleClickout() {
	// Hardcoded sports campaigns: trackClickout() no-ops for sports urls (uct_sport set), so track manually here.
	if (props.sportsTargetEvent && offer.value && clickoutUrl.value) {
		trackClickOutSports(
			offer.value,
			undefined,
			undefined,
			props.sportsTargetEvent.sportObjectType ?? SportObjectType.MainEvent,
			props.sportsTargetEvent.offerLocation ?? 'watchnow',
			sportsContext.value ? [sportsContext.value, ...props.additionalContexts] : props.additionalContexts,
			placement,
			clickoutType
		);
		return;
	}

	if (offer.value.package.shortName === 'jwt' && similarTitle.value && clickoutUrl.value) {
		trackClickout(clickoutUrl.value);

		if (isJWTVRentalOffer(offer.value)) {
			openRentalModal({
				title: similarTitle.value?.node,
				offer: offer.value,
				clickoutUrl: clickoutUrl.value!,
				overrideRedirectUrl: true,
			});
			return;
		} else {
			await openJwTvAvodModal({
				modalProps: {
					offer: offer.value,
					title: similarTitle.value?.node,
					additionalContexts: getClickoutUrlContextsArray(clickoutUrl.value),
				},
			});
		}
	}
}

// Type guard, since we now use this component to handle customTargetTitle
function isSimilarTitlesEdge(value: unknown): value is SimilarTitlesEdge & { similarityScore?: Maybe<number> } {
	return (
		value != null && typeof value === 'object' && '__typename' in value && value.__typename === 'SimilarTitlesEdge'
	);
}

return { __sfc: true,props, country, webLocale, xServeTrack, sqtvClickoutType, clickoutType, placement, setSQTVFallbackTag, setLinearTVExpTags, setTitlePageCategorizationExpTags, titleContext, filterOption, adServerIds, adServerStatus, providerShortName, singleQtvRef, isJWTVOffer, hasFreeTrial, isFreeOrFreeTrial, _similarTitle, loading, sqtvBannerAndDisclaimer, getSQTVTags, isAwaitingContent, similarTitle, scores, excludedScores, sourcePosterUrl, posterUrl, backdropUrl, votesNumber, offer, jwRawEntityId, getQTVCompositionId, compositionId, similarityScore, recommenderContext, similarTitleContext, sportsContext, clickoutUrl, tags, observeVisibilityOptions, baseContexts, recommenderImpressionContexts, offerImpressionContexts, onIntersection, onVisibilityChanged, imageUrl, openRentalModal, handleClickout, isSimilarTitlesEdge, PictureComp, TitleScoreList, ProviderIcon, PromotionRowCTA, ClickoutAnchor, DisclaimerButton }
}

})